// src/services/corrections.service.ts

import apiClient from '@/services/api/client';

export type CorrectionStatus = 'pendiente' | 'leida' | 'atendida';

export interface Correction {
  id: string;
  documentId:   string;
  documentName: string;
  documentCode: string;
  message:      string;
  sentByUid:    string;
  sentByName:   string;
  sentToUid:    string;
  sentToName:   string;
  sentAt:       string;
  status:       CorrectionStatus;
}

class CorrectionsService {
  /**
   * Crea una nueva corrección.
   */
  async sendCorrection(data: Omit<Correction, 'id' | 'status' | 'sentAt'>): Promise<string> {
    const response = await apiClient.post('/corrections', data);
    return response.data.data.id as string;
  }

  /**
   * Correcciones pendientes para un Responsable (sentToUid).
   */
  async getForResponsable(uid: string): Promise<Correction[]> {
    const response = await apiClient.get('/corrections', {
      params: { sentToUid: uid }
    });
    return response.data.data as Correction[];
  }

  /**
   * Suscripción con polling a correcciones pendientes para un Responsable.
   */
  onCorrectionsForResponsable(uid: string, cb: (items: Correction[]) => void, intervalMs: number = 15000): () => void {
    let active = true;
    let timer: number | undefined;

    const fetchItems = async () => {
      if (!active) return;
      try {
        const items = await this.getForResponsable(uid);
        cb(items.filter((i) => i.status === 'pendiente'));
      } catch (err) {
        console.error('[corrections] polling error:', err);
      } finally {
        if (active) timer = window.setTimeout(fetchItems, intervalMs);
      }
    };

    fetchItems();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }

  /**
   * Actualiza el estado de una corrección.
   */
  async updateStatus(id: string, status: CorrectionStatus): Promise<void> {
    await apiClient.put(`/corrections/${id}`, { status });
  }

  /**
   * Suscripción con polling a TODAS las correcciones para un Responsable.
   */
  onAllCorrectionsForResponsable(uid: string, cb: (items: Correction[]) => void, intervalMs: number = 15000): () => void {
    let active = true;
    let timer: number | undefined;

    const fetchItems = async () => {
      if (!active) return;
      try {
        const items = await this.getForResponsable(uid);
        cb(items);
      } catch (err) {
        console.error('[corrections] polling error:', err);
      } finally {
        if (active) timer = window.setTimeout(fetchItems, intervalMs);
      }
    };

    fetchItems();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }
}

export const correctionsService = new CorrectionsService();
